import { useQuery } from '@tanstack/react-query';

import { useApiServices } from '../api/services';
import type { NavCounts } from '../api/nav-counts';
import { useAuth } from './useAuth';

/**
 * The sidebar's badge counts — the first data call after the session is
 * established, since the sidebar renders on every screen.
 *
 * Keyed by org id so switching organizations swaps counts instead of showing
 * the previous org's numbers. The server caches for 30s, so `staleTime` is
 * matched to it: within that window a refetch would only re-read the same
 * cached row, and beyond it the query is cheap enough to just re-run.
 */
export function useNavCounts() {
  const { organization } = useAuth();
  const { navCounts } = useApiServices();
  const orgId = organization?.id;

  return useQuery<NavCounts>({
    enabled: !!orgId,
    queryKey: ['nav-counts', orgId],
    queryFn: () => navCounts.get(),
    staleTime: 30_000,
    // A failed badge fetch must never surface as an error state — the sidebar
    // just renders without badges.
    refetchOnWindowFocus: true,
  });
}
